
import { defineComponent, ref, PropType, computed, shallowRef, watch, getCurrentInstance, onMounted } from 'vue'
import useRouter from '../../../../hooks/useRouter'
import { Project } from '../../../../types/main'
import get from 'lodash/get'
import omit from 'lodash/omit'
import merge from 'lodash/merge'
import isString from 'lodash/isString'
import isPlainObject from 'lodash/isPlainObject'
import isNil from 'lodash/isNil'
import cloneDeep from 'lodash/cloneDeep'
import BaseInputOutlined from '../../../inputs/BaseInputOutlined.vue'
import CustomActivatorSelect from '../../../inputs/CustomActivatorSelect.vue'
import TimeInput from '../../../inputs/TimeInput.vue'
import { ActivationToEdit, EditedActivation } from '../../../../types/GCB2'
import { CONNECTOR_TYPES, MESSAGING_CONNECTOR_TYPES, CHANNEL_TYPES } from '../../../../vars/general'
import api from '@root/src/api/api'
import cfg from '@root/config'
import { getCascadesByProject } from '@/api/networkFixer'

export default defineComponent({
    name: 'BotSettings',
    components: { BaseInputOutlined, CustomActivatorSelect, TimeInput },
    props: {
        value: { type: Object as PropType<EditedActivation | ActivationToEdit>, required: true },
        botsList: { type: Array as PropType<any>, required: false, default: null },
    },
    setup(props, { emit }) {
        const root = getCurrentInstance()!.proxy
        const { route } = useRouter(root)
        const project = computed<Project>(() => root.$store.getters.projectById(route.value.params.id))
        const preferedBot = computed<String>(() => get(props.value.extra, 'preferedBot') ?? 'default')
        const formInput = (e: any) => {
            // Валидация: если isChannelsRework, проверяем наличие sendingSettings с id
            // Иначе всегда валидно (поле необязательное)
            const sendingSettings = get(props.value.extra, 'sendingSettings') ?? null
            const isValid = project.value.isChannelsRework ? (!sendingSettings || Boolean(sendingSettings.id)) : true
            emit('validation-updated', isValid)
        }
        const listLoading = ref(false)
        const changePreferedBot = (preferedBot: String) => {
            let newVal = null
            if (preferedBot === 'default') {
                newVal = Object.assign({}, props.value, {
                    extra: omit(props.value.extra, 'preferedBot'),
                })
            } else {
                newVal = Object.assign({}, props.value, {
                    extra: merge(props.value.extra, { preferedBot: preferedBot }),
                })
            }
            emit('input', newVal)
            // Обновляем валидацию после изменения
            formInput(null)
        }
        const connectorsHashMapWithKeysByConnectorsType = computed(
            () => root.$store.getters.connectorsHashMapWithKeysByConnectorsType
        )
        let availableBots = ref([{ id: 'default', name: 'Не определено' }])
        
        // Логика для каналов (если isChannelsRework)
        const channels = computed(() => {
            if (!project.value?.isChannelsRework) return []
            
            const channelTypes = Object.values(CHANNEL_TYPES)
            return project.value.connectors.filter(c => 
                !c.isChisai && 
                c.connectorType !== CONNECTOR_TYPES.whatsappBasis &&
                channelTypes.includes(c.connectorType)
            )
        })
        
        const channelGroups = computed(() => [
            {
                name: 'whatsapp',
                channelTypes: [CHANNEL_TYPES.whatsappChannel],
            },
            {
                name: 'telegram',
                channelTypes: [CHANNEL_TYPES.telegramChannel],
            },
            {
                name: 'max',
                channelTypes: [CHANNEL_TYPES.maxChannel],
            },
        ])
        
        const getChannelIndex = (connectorType: string, connectorId: string) => {
            const group = channelGroups.value.find(g => g.channelTypes.includes(connectorType))
            if (!group) return 1

            // Индексация как в таблице каналов (ConnectorsListView): считаем только connectorType из CHANNEL_TYPES,
            // по всем коннекторам группы (включая DH), чтобы имена совпадали.
            const sortedChannels = (project.value?.connectors || [])
                .filter(c => !c.isChisai && c.connectorType === connectorType)
                .sort((a, b) => {
                    const aTime = a.createdAt && a.createdAt > 0 ? a.createdAt : Number.MAX_SAFE_INTEGER
                    const bTime = b.createdAt && b.createdAt > 0 ? b.createdAt : Number.MAX_SAFE_INTEGER
                    if (aTime !== Number.MAX_SAFE_INTEGER && bTime !== Number.MAX_SAFE_INTEGER) {
                        return aTime - bTime
                    }
                    if (aTime === Number.MAX_SAFE_INTEGER && bTime !== Number.MAX_SAFE_INTEGER) return 1
                    if (aTime !== Number.MAX_SAFE_INTEGER && bTime === Number.MAX_SAFE_INTEGER) return -1
                    return a.connectorId.localeCompare(b.connectorId)
                })

            const currentPosition = sortedChannels.findIndex(c => c.connectorId === connectorId)
            return currentPosition >= 0 ? currentPosition + 1 : 1
        }
        
        const getChannelDisplayName = (connectorType: string, index: number) => {
            const baseNames = {
                [CHANNEL_TYPES.whatsappChannel]: 'WhatsApp',
                [CHANNEL_TYPES.telegramChannel]: 'Telegram',
                [CHANNEL_TYPES.maxChannel]: 'MAX',
            }
            const baseName = baseNames[connectorType] || connectorType
            
            if (index === 1) {
                return baseName
            }
            return `${baseName} ${index}`
        }

        const getCustomCascadeCreatedAtTimestamp = (cascade: any) => {
            const ts = cascade?.createdAt ? new Date(cascade.createdAt).getTime() : Number.MAX_SAFE_INTEGER
            return Number.isNaN(ts) ? Number.MAX_SAFE_INTEGER : ts
        }
        const sortCustomCascadesByCreatedAt = (list: any[]) => {
            return [...(list || [])].sort((a: any, b: any) => {
                const aTime = getCustomCascadeCreatedAtTimestamp(a)
                const bTime = getCustomCascadeCreatedAtTimestamp(b)
                if (aTime !== bTime) return aTime - bTime
                return String(a?.id ?? '').localeCompare(String(b?.id ?? ''))
            })
        }
        const getCustomCascadeNumber = (cascadeId: string | number) => {
            const customCascades = sortCustomCascadesByCreatedAt(
                cascades.value.filter((c: any) => c && c.cascadeType === 'custom'),
            )
            const customIndex = customCascades.findIndex((c: any) => String(c.id) === String(cascadeId))
            return customIndex >= 0 ? customIndex + 2 : null
        }
        const getCustomCascadeLabel = (cascade: any) => {
            const number = getCustomCascadeNumber(cascade?.id)
            const name = cascade?.name ? String(cascade.name).trim() : ''
            if (!number) return 'Каскад'
            return name ? `Каскад ${number} - ${name}` : `Каскад ${number}`
        }

        const cascades = ref<any[]>([])
        const gcb2Connector = computed(() =>
            project.value?.connectors?.find((el: any) => el.connectorType === CONNECTOR_TYPES.GCB2),
        )
        const gcb2DefaultActivationSendChannel = computed(() => {
            return (
                get(gcb2Connector.value, 'settings.defaultActivationSendChannel') ??
                get(gcb2Connector.value, 'defaultActivationSendChannel') ??
                null
            )
        })
        const normalizeActivationSendChannel = (raw: any) => {
            if (!raw) return null
            let val = raw
            if (isString(val)) {
                try {
                    val = JSON.parse(val)
                } catch (e) {
                    // ignore
                }
            }
            if (!isPlainObject(val)) return null
            const id =
                !isNil((val as any).id)
                    ? (val as any).id
                    : !isNil((val as any).channelId)
                        ? (val as any).channelId
                        : !isNil((val as any).connectorId)
                            ? (val as any).connectorId
                            : null
            if (isNil(id)) return null
            const type = (val as any).type || null
            return { type, id }
        }
        const defaultOptionLabel = computed(() => {
            const def = normalizeActivationSendChannel(gcb2DefaultActivationSendChannel.value)
            if (!def) return 'По умолчанию'

            if (def.type === 'cascade') {
                const found = cascades.value.find(c => c && String(c.id) === String(def.id))
                const cascadeLabel =
                    found?.cascadeType === 'custom'
                        ? getCustomCascadeLabel(found)
                        : 'Каскад'
                return `По умолчанию (${cascadeLabel})`
            }

            const channelConnector =
                project.value?.connectors?.find(
                    (c: any) => String(c.channelId || c.connectorId) === String(def.id),
                ) || null
            if (channelConnector) {
                const connectorType = channelConnector.connectorType
                const index = getChannelIndex(connectorType, channelConnector.connectorId)
                const displayName = getChannelDisplayName(connectorType, index)
                const phone = (channelConnector as any).phone
                const channelLabel = phone ? `${displayName} (${phone})` : displayName
                return `По умолчанию (${channelLabel})`
            }

            return 'По умолчанию'
        })
        
        const channelOptions = computed(() => {
            const options: any[] = []

            // Значение по умолчанию (если в активации нет sendingSettings)
            options.push({ label: defaultOptionLabel.value, value: 'default' })
            
            // Добавляем каналы
            channels.value.forEach(connector => {
                const connectorType = connector.connectorType
                const index = getChannelIndex(connectorType, connector.connectorId)
                const displayName = getChannelDisplayName(connectorType, index)
                const channelId = connector.channelId || connector.connectorId
                const phone = (connector as any).phone
                const label = phone ? `${displayName} (${phone})` : displayName
                
                options.push({
                    label,
                    value: JSON.stringify({ type: 'channel', id: channelId })
                })
            })

            // Добавляем каскады (для активаций — mailings + custom)
            const cascadesForOptions = cascades.value
                .filter(c => c && (c.cascadeType === 'mailings' || c.cascadeType === 'custom'))
                .filter(c => c.cascadeType !== 'custom' || (Array.isArray(c.channels) && c.channels.length > 0))
            const sortedCustomCascades = sortCustomCascadesByCreatedAt(
                cascadesForOptions.filter(c => c.cascadeType === 'custom'),
            )
            const sortedCascadesForOptions = [
                ...cascadesForOptions.filter(c => c.cascadeType !== 'custom'),
                ...sortedCustomCascades,
            ]
            sortedCascadesForOptions.forEach(c => {
                const label = c.cascadeType === 'custom' ? getCustomCascadeLabel(c) : 'Каскад'
                options.push({
                    label,
                    value: JSON.stringify({ type: 'cascade', id: c.id }),
                })
            })
            
            return options
        })
        
        const selectedChannel = computed(() => {
            const sendingSettings = get(props.value.extra, 'sendingSettings')
            if (!sendingSettings || !sendingSettings.id) return 'default'
            
            const channelId = sendingSettings.id
            const options = channelOptions.value
            
            // Ищем соответствующее значение в channelOptions по id
            const matchingOption = options.find(option => {
                if (!option.value) return false
                try {
                    const optionData = JSON.parse(option.value)
                    return String(optionData.id) === String(channelId)
                } catch (e) {
                    return false
                }
            })
            
            // Если нашли совпадение, возвращаем его value
            if (matchingOption) {
                return matchingOption.value
            }
            
            // Если не нашли, пытаемся создать значение из сохраненных данных
            return JSON.stringify(sendingSettings)
        })
        
        const changeChannel = (value: string | null) => {
            let newVal = null
            if (value === null || value === 'default') {
                newVal = Object.assign({}, props.value, {
                    extra: omit(props.value.extra, 'sendingSettings'),
                })
            } else {
                try {
                    const channelData = JSON.parse(value)
                    newVal = Object.assign({}, props.value, {
                        extra: merge(props.value.extra, { sendingSettings: channelData }),
                    })
                } catch (e) {
                    console.error('Ошибка при парсинге канала:', e)
                    newVal = Object.assign({}, props.value, {
                        extra: omit(props.value.extra, 'sendingSettings'),
                    })
                }
            }
            emit('input', newVal)
            // Обновляем валидацию после изменения
            formInput(null)
        }
        
        const clear = () => {
            (root.$refs as any).form.resetValidation()
            if (project.value.isChannelsRework) {
                changeChannel(null)
            } else {
                changePreferedBot('default')
            }
        }

        const loadCascades = async () => {
            if (!project.value?.isChannelsRework) return
            const projectId = project.value?.projectId || project.value?.id || root.$router.currentRoute.params.id
            if (!projectId) return
            try {
                const { data: projectCascades, error } = await getCascadesByProject(projectId)
                if (!error && projectCascades) {
                    cascades.value = projectCascades
                } else {
                    cascades.value = []
                }
            } catch (e) {
                cascades.value = []
            }
        }
        onMounted(async () => {
            await loadCascades()
            listLoading.value = true
            const filteredConnectors = project.value.connectors.filter(el =>
                {   let connectorTypes = [...MESSAGING_CONNECTOR_TYPES]
                    if (cfg.botYcAvailable.includes(project.value.id)) {
                        connectorTypes.push(CONNECTOR_TYPES.yClients)
                    }
                    return connectorTypes.includes(el.connectorType)
                }
            )
            if (props.botsList) {
                availableBots.value = cloneDeep(props.botsList)
            } else {
                for (const item of filteredConnectors) {
                    let name = ''
                    const isSpareConnector = item?.connectorId?.includes('spare')
                    if (item.connectorType === CONNECTOR_TYPES.whatsappBasis) {
                        const [app, error] = await api.networkFixer
                            .getApp({ connectorId: item.connectorId })
                            .then(res => [res.data?.data, res.error])
                        if (app?.channel?.['wApi-telegram']?.credentials || app?.channel?.['dh-telegram']?.credentials) {
                            name = 'Telegram'
                        } else if (app?.channel?.['green-api-max']?.credentials) {
                            name = 'MAX'
                        } else {
                            name = 'Whatsapp'
                        }
                    } else {
                        name =
                            connectorsHashMapWithKeysByConnectorsType.value[item.connectorType].name ??
                            item.connectorType
                    }
                    availableBots.value.push({
                        id: item.connectorId,
                        name: isSpareConnector ? `${name} (запасной)` : name,
                    })
                }
            }
            listLoading.value = false
            // Вызываем валидацию после загрузки данных
            formInput(null)
        })

        // Watch на изменение значения для обновления валидации
        watch(
            () => [props.value.extra?.sendingSettings, project.value?.isChannelsRework],
            () => {
                formInput(null)
            },
            { deep: true }
        )

        return { 
            project, 
            availableBots, 
            preferedBot, 
            formInput, 
            changePreferedBot, 
            listLoading, 
            clear,
            channelOptions,
            selectedChannel,
            changeChannel
        }
    },
})
